import React, { useContext, useState } from 'react'
import { assign } from 'lodash'
import { FacebookLogin } from 'react-facebook-login-component'
import i18next from '../../../i18n'
import { TextFieldControl } from '../../utils'
import { required, email } from '../../validations/fieldLevelValidations'
import { useForm } from 'react-hook-form'
import { Button, Card, CircularProgress, createStyles, Divider, Grid, makeStyles, Typography, Link, Menu, MenuItem } from '@material-ui/core'
import { AuthContext } from '../../Providers/AuthProvider'
import { ConversationContext } from '../../Providers/ConversationProvider'
import { TopicContext } from '../../Providers/TopicProvider'
import ResetPassword from '../../password-resets/CreateContainer'
import { useTranslation } from 'react-i18next'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { useAuth } from '../../Providers'
import LoadingAnimation from '../../shared/LoadingAnimation'
import GoogleLogin from './GoogleLogin'
import { LoginErrorKind, loginErrorKind } from './loginError'

interface Props {
  title?: string
  onLogin?()
  onSignUp?()
  hosting?: boolean
  sso?: boolean
}
const SsoLogin = () => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  return (
    <>
      <Button aria-controls='simple-menu' aria-haspopup='true' onClick={handleClick}>
        Sign in with SSO
      </Button>
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        getContentAnchorEl={null}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem>
          <a href={window.OKTA_LOGIN_URL}>Okta</a>
        </MenuItem>
      </Menu>
    </>
  )
}

const LoginForm = ({ onLogin, title, onSignUp, hosting, sso = false }: Props) => {
  const { handleSubmit, errors, formState, control } = useForm({ mode: 'onBlur', shouldUnregister: false })
  const { isDirty, isSubmitting } = formState
  const { loaded } = useAuth()

  const { logInUser, loginWithFacebook } = useContext(AuthContext)
  const { conversation } = useContext(ConversationContext)
  const { topic } = useContext(TopicContext)
  const [loading, setloading] = useState(false)
  const [errorKind, setErrorKind] = useState<LoginErrorKind | null>(null)
  const { t, i18n } = useTranslation()

  const loginComplete = () => {
    setloading(false)
    onLogin && onLogin()
  }

  const handleSave = handleSubmit((data: any, e) => {
    setloading(true)
    setErrorKind(null)
    logInUser(data, { locale: i18n.language })
      .then(loginComplete)
      .catch((error) => {
        setloading(false)
        // The server says WHICH failure this is; classify it instead of
        // printing the password message over both. See ./loginError.
        setErrorKind(loginErrorKind(error))
      })
  })

  const builtValues = (data) => {
    return assign({}, data, {
      conversationRoleInterest: { hosting },
      signedUpTopicId: topic?.id,
      signedUpConversationId: conversation?.id,
      locale: i18next.language
    })
  }

  const responseFacebook = (response) => {
    const modal = window.location.pathname === '/join' ? false : true
    loginWithFacebook(builtValues(response)).then(loginComplete)
  }
  const linkClass = 'link-text'

  const classes = Styles()

  // const title = conversationInviteLink ? t('auth.inviteLogin') : t('auth.login')
  if (!loaded) {
    return <LoadingAnimation />
  }
  return (
    <div className={classes.root}>
      <h3 className={classes.header}>{title || t('auth.login')}</h3>
      <div className='align-center'>
        <p>
          {t('auth.noAccount')}&nbsp;
          <a className={linkClass} onClick={onSignUp}>
            {t('auth.signupHere')}
          </a>
        </p>
      </div>
      {errorKind && (
        <div style={{ backgroundColor: '#FAEBEB', padding: '.5rem', borderRadius: '.5rem' }}>
          <Typography variant='body1' color='error' align='center' component='div' style={{ display: 'flex', justifyContent: 'center' }} role='alert'>
            <HighlightOffIcon style={{ fontSize: '1rem', marginRight: '.25rem', marginTop: '.25rem' }} />
            {errorKind === 'sso' ? t('auth.ssoAccountUseGoogle') : t('auth.badCredentials')}
          </Typography>
        </div>
      )}

      <form onSubmit={handleSave}>
        <TextFieldControl
          label={t('common.email')}
          rules={{ validate: { required, email } }}
          name='email'
          control={control}
          error={errors && errors.email}
          className='fat-top'
          disabled={loading}
        />
        <TextFieldControl
          label={t('auth.password')}
          rules={{ validate: { required } }}
          name='password'
          control={control}
          type='password'
          error={errors && errors.password}
          className='fat-top'
          disabled={loading}
        />
        <div className={classes.actionContainer}>
          <Button size='large' disabled={!isDirty || isSubmitting || loading} type='submit' color='primary' variant='contained' className={classes.loginButton}>
            {!loading && t('auth.login')}
            {loading && <CircularProgress color='secondary' />}
          </Button>
        </div>
      </form>
      <div className={classes.forgotPassword}>
        <Grid container justify='center'>
          <Grid item xs={12} sm={6}>
            <ResetPassword />
          </Grid>
          {sso && (
            <Grid item xs={12} sm={6}>
              <SsoLogin />
            </Grid>
          )}
        </Grid>
      </div>

      <div className='align-center'>
        <div className={classes.divider}>
          <Grid container justify='center' alignItems='center' spacing={1}>
            <Grid item xs={5}>
              <Divider />
            </Grid>
            <Grid item>{t('common.or')}</Grid>
            <Grid item xs={5}>
              <Divider />
            </Grid>
          </Grid>
        </div>
        <div className='facebook-login-btn'>
          <FacebookLogin
            socialId={process.env.REACT_APP_FACEBOOK_APP_ID}
            language={i18next.language}
            scope='public_profile, email'
            responseHandler={responseFacebook}
            xfbml={true}
            version='v2.8'
            buttonText={t('auth.fbLogin')}
            fields='id, email, first_name, last_name, picture'
          />
        </div>
      </div>
      <div className='align-center reg-top'>
        <div className={classes.divider}>
          <Grid container justify='center' alignItems='center' spacing={1}>
            <Grid item xs={5}>
              <Divider />
            </Grid>
            <Grid item>{t('common.or')}</Grid>
            <Grid item xs={5}>
              <Divider />
            </Grid>
          </Grid>
        </div>
        <GoogleLogin loginComplete={loginComplete} />
      </div>
    </div>
  )
}

const Styles = makeStyles((theme) =>
  createStyles({
    root: {
      margin: '2em'
    },
    header: {
      textAlign: 'center',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '24px',
      lineHeight: '32px'
    },
    actionContainer: {
      maxWidth: '304px',
      margin: '1em auto',
      textAlign: 'center'
    },
    divider: {
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '21px',
      color: '#6D6E71',
      paddingBottom: '1em'
    },
    loginButton: {
      marginTop: '1em',
      width: '265px',
      height: '48px',
      fontStyle: 'normal',
      fontWeight: 800,
      fontSize: '18px',
      lineHeight: '26px'
    },
    forgotPassword: {
      textAlign: 'center',
      margin: '1em 0'
    }
  })
)

export default LoginForm
